import { supabase } from './client';
import type {
  Category,
  MenuItem,
  MenuItemWithCategory,
  CreateMenuItemData,
  UpdateMenuItemData,
  WineCategory,
  WineItem,
  WineItemWithCategory,
  CreateWineItemData,
  UpdateWineItemData,
} from './types';

/**
 * Fetch all categories ordered by display_order
 */
export async function fetchCategories(): Promise<Category[]> {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, name_is, display_order, created_at')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching categories:', error);
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchCategories:', error);
    throw error;
  }
}

/**
 * Fetch all menu items with their category information
 */
export async function fetchMenuItems(): Promise<MenuItemWithCategory[]> {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select(
        `
        id, name, name_is, description, description_is, price, category_id, image_url, available_status, is_highlight, display_order, created_at, last_modified_date,
        category:categories(*)
      `
      )
      .eq('available_status', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching menu items:', error);
      throw new Error(`Failed to fetch menu items: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchMenuItems:', error);
    throw error;
  }
}

/**
 * Fetch menu items by category
 */
export async function fetchMenuItemsByCategory(categoryId: string): Promise<MenuItem[]> {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select(
        'id, name, name_is, description, description_is, price, category_id, image_url, available_status, is_highlight, display_order, created_at, last_modified_date'
      )
      .eq('category_id', categoryId)
      .eq('available_status', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching menu items by category:', error);
      throw new Error(`Failed to fetch menu items by category: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchMenuItemsByCategory:', error);
    throw error;
  }
}

/**
 * Fetch a single menu item by ID
 */
export async function fetchMenuItemById(id: string): Promise<MenuItemWithCategory | null> {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select(
        `
        id, name, name_is, description, description_is, price, category_id, image_url, available_status, is_highlight, display_order, created_at, last_modified_date,
        category:categories(*)
      `
      )
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No rows returned
      }
      console.error('Error fetching menu item by ID:', error);
      throw new Error(`Failed to fetch menu item: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in fetchMenuItemById:', error);
    throw error;
  }
}

/**
 * Create a new menu item
 */
export async function createMenuItem(item: CreateMenuItemData): Promise<MenuItem> {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .insert([
        {
          ...item,
          available_status: item.available_status ?? true,
          created_at: new Date().toISOString(),
          last_modified_date: new Date().toISOString(),
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Error creating menu item:', error);
      throw new Error(`Failed to create menu item: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in createMenuItem:', error);
    throw error;
  }
}

/**
 * Update an existing menu item
 */
export async function updateMenuItem(id: string, updates: UpdateMenuItemData): Promise<MenuItem> {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .update({
        ...updates,
        last_modified_date: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating menu item:', error);
      throw new Error(`Failed to update menu item: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in updateMenuItem:', error);
    throw error;
  }
}

/**
 * Delete a menu item
 */
export async function deleteMenuItem(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('menu_items').delete().eq('id', id);

    if (error) {
      console.error('Error deleting menu item:', error);
      throw new Error(`Failed to delete menu item: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in deleteMenuItem:', error);
    throw error;
  }
}

/**
 * Toggle menu item availability
 */
export async function toggleMenuItemAvailability(id: string): Promise<MenuItem> {
  try {
    // First fetch the current status
    const { data: currentItem, error: fetchError } = await supabase
      .from('menu_items')
      .select('available_status')
      .eq('id', id)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch current status: ${fetchError.message}`);
    }

    // Toggle the status
    const { data, error } = await supabase
      .from('menu_items')
      .update({
        available_status: !currentItem.available_status,
        last_modified_date: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error toggling menu item availability:', error);
      throw new Error(`Failed to toggle menu item availability: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in toggleMenuItemAvailability:', error);
    throw error;
  }
}

/**
 * Fetch featured menu items (highlighted items for homepage preview)
 */
export async function fetchFeaturedMenuItems(): Promise<MenuItemWithCategory[]> {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select(
        `
        id, name, name_is, description, description_is, price, category_id, image_url, available_status, is_highlight, display_order, created_at, last_modified_date,
        category:categories(*)
      `
      )
      .eq('available_status', true)
      .eq('is_highlight', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching featured menu items:', error);
      throw new Error(`Failed to fetch featured menu items: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchFeaturedMenuItems:', error);
    throw error;
  }
}

/**
 * Toggle menu item highlight status
 */
export async function toggleMenuItemHighlight(id: string): Promise<MenuItem> {
  try {
    // First fetch the current status
    const { data: currentItem, error: fetchError } = await supabase
      .from('menu_items')
      .select('is_highlight')
      .eq('id', id)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch current highlight status: ${fetchError.message}`);
    }

    // Toggle the highlight status
    const { data, error } = await supabase
      .from('menu_items')
      .update({
        is_highlight: !currentItem.is_highlight,
        last_modified_date: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error toggling menu item highlight:', error);
      throw new Error(`Failed to toggle menu item highlight: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in toggleMenuItemHighlight:', error);
    throw error;
  }
}

/**
 * Get count of highlighted menu items
 */
export async function getHighlightedItemsCount(): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true })
      .eq('available_status', true)
      .eq('is_highlight', true);

    if (error) {
      console.error('Error counting highlighted items:', error);
      throw new Error(`Failed to count highlighted items: ${error.message}`);
    }

    return count || 0;
  } catch (error) {
    console.error('Error in getHighlightedItemsCount:', error);
    throw error;
  }
}

// ===== WINE LIST API FUNCTIONS =====

/**
 * Fetch all wine categories ordered by display_order
 */
export async function fetchWineCategories(): Promise<WineCategory[]> {
  try {
    const { data, error } = await supabase
      .from('wine_categories')
      .select('id, name, name_is, display_order, created_at')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching wine categories:', error);
      throw new Error(`Failed to fetch wine categories: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchWineCategories:', error);
    throw error;
  }
}

/**
 * Fetch all wine items with their category information
 */
export async function fetchWineItems(): Promise<WineItemWithCategory[]> {
  try {
    const { data, error } = await supabase
      .from('wine_items')
      .select(
        `
        id, name, name_is, description, description_is, price, small_bottle_price, wine_category_id, image_url, year, region, available_status, display_order, sub_category, created_at, last_modified_date,
        wine_category:wine_categories(*)
      `
      )
      .eq('available_status', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching wine items:', error);
      throw new Error(`Failed to fetch wine items: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchWineItems:', error);
    throw error;
  }
}

/**
 * Fetch wine items by category
 */
export async function fetchWineItemsByCategory(categoryId: string): Promise<WineItem[]> {
  try {
    const { data, error } = await supabase
      .from('wine_items')
      .select(
        'id, name, name_is, description, description_is, price, small_bottle_price, wine_category_id, image_url, year, region, available_status, display_order, created_at, last_modified_date'
      )
      .eq('wine_category_id', categoryId)
      .eq('available_status', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching wine items by category:', error);
      throw new Error(`Failed to fetch wine items by category: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchWineItemsByCategory:', error);
    throw error;
  }
}

/**
 * Fetch a single wine item by ID
 */
export async function fetchWineItemById(id: string): Promise<WineItemWithCategory | null> {
  try {
    const { data, error } = await supabase
      .from('wine_items')
      .select(
        `
        id, name, name_is, description, description_is, price, small_bottle_price, wine_category_id, image_url, year, region, available_status, display_order, sub_category, created_at, last_modified_date,
        wine_category:wine_categories(*)
      `
      )
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No rows returned
      }
      console.error('Error fetching wine item by ID:', error);
      throw new Error(`Failed to fetch wine item: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in fetchWineItemById:', error);
    throw error;
  }
}

/**
 * Create a new wine item
 */
export async function createWineItem(item: CreateWineItemData): Promise<WineItem> {
  try {
    const { data, error } = await supabase
      .from('wine_items')
      .insert([
        {
          ...item,
          available_status: item.available_status ?? true,
          created_at: new Date().toISOString(),
          last_modified_date: new Date().toISOString(),
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Error creating wine item:', error);
      throw new Error(`Failed to create wine item: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in createWineItem:', error);
    throw error;
  }
}

/**
 * Update an existing wine item
 */
export async function updateWineItem(id: string, updates: UpdateWineItemData): Promise<WineItem> {
  try {
    const { data, error } = await supabase
      .from('wine_items')
      .update({
        ...updates,
        last_modified_date: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating wine item:', error);
      throw new Error(`Failed to update wine item: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in updateWineItem:', error);
    throw error;
  }
}

/**
 * Delete a wine item
 */
export async function deleteWineItem(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('wine_items').delete().eq('id', id);

    if (error) {
      console.error('Error deleting wine item:', error);
      throw new Error(`Failed to delete wine item: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in deleteWineItem:', error);
    throw error;
  }
}

/**
 * Toggle wine item availability
 */
export async function toggleWineItemAvailability(id: string): Promise<WineItem> {
  try {
    // First fetch the current status
    const { data: currentItem, error: fetchError } = await supabase
      .from('wine_items')
      .select('available_status')
      .eq('id', id)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch current status: ${fetchError.message}`);
    }

    // Toggle the status
    const { data, error } = await supabase
      .from('wine_items')
      .update({
        available_status: !currentItem.available_status,
        last_modified_date: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error toggling wine item availability:', error);
      throw new Error(`Failed to toggle wine item availability: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in toggleWineItemAvailability:', error);
    throw error;
  }
}

/**
 * Fetch site settings (hero image, about section images)
 */
export async function fetchSiteSettings(): Promise<SiteSettings | null> {
  try {
    const { data, error } = await supabase.from('site_settings').select('*').single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.error('No site settings found');
        return null;
      }
      console.error('Error fetching site settings:', error);
      throw new Error(`Failed to fetch site settings: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error in fetchSiteSettings:', error);
    throw error;
  }
}

/**
 * Update site settings
 */
export async function updateSiteSettings(updates: UpdateSiteSettingsData): Promise<SiteSettings> {
  try {
    // First check if a record exists
    const { data: existingData, error: checkError } = await supabase
      .from('site_settings')
      .select('id')
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking site settings:', checkError);
      throw new Error(`Failed to check site settings: ${checkError.message}`);
    }

    let result;

    if (existingData) {
      // Update existing record
      const { data, error } = await supabase
        .from('site_settings')
        .update(updates)
        .eq('id', existingData.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating site settings:', error);
        throw new Error(`Failed to update site settings: ${error.message}`);
      }

      result = data;
    } else {
      // Insert new record
      const { data, error } = await supabase
        .from('site_settings')
        .insert([updates])
        .select()
        .single();

      if (error) {
        console.error('Error creating site settings:', error);
        throw new Error(`Failed to create site settings: ${error.message}`);
      }

      result = data;
    }

    return result;
  } catch (error) {
    console.error('Error in updateSiteSettings:', error);
    throw error;
  }
}
